// src/pages/HeathrowHotels.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../styles/heathrowHotels.css";

// Sample hotel data - easy to extend
const hotels = [
  {
    name: "Sofitel London Heathrow (Terminal 5)",
    rating: 4.6,
    price: "£140 – £280/night",
    distance: "Inside Terminal 5 (0 miles)",
    taxiFare: "£18 – £28 from any Heathrow terminal",
    facilities: ["Free WiFi", "Spa", "Gym", "Restaurant", "Bar", "Conference Rooms"],
    pros: [
      "Direct access to Terminal 5",
      "Luxury rooms",
      "Soundproof and comfortable",
      "Ideal for business travellers",
    ],
    cons: ["More expensive than nearby hotels"],
  },
  {
    name: "Hilton London Heathrow Airport Terminal 5",
    rating: 4.3,
    price: "£110 – £220/night",
    distance: "1.2 miles from Terminal 5",
    taxiFare: "£18 – £26",
    facilities: ["Free WiFi", "Gym", "Spa", "Restaurant", "Bar"],
    pros: ["Large rooms", "Great facilities", "Good for families"],
    cons: ["Not walkable to terminals"],
  },
  {
    name: "Premier Inn Heathrow Terminal 4",
    rating: 4.4,
    price: "£65 – £120/night",
    distance: "0.2 miles (connected walkway)",
    taxiFare: "£16 – £24",
    facilities: ["Free WiFi", "Restaurant", "Family Rooms"],
    pros: ["Excellent budget choice", "Clean and modern", "Walkway to T4"],
    cons: ["Food options limited at late hours"],
  },
  {
    name: "Novotel London Heathrow Airport T1 T2 T3",
    rating: 4.0,
    price: "£90 – £150/night",
    distance: "1.4 miles from Terminals 2–3",
    taxiFare: "£18 – £26",
    facilities: ["Restaurant", "Bar", "Gym", "Parking"],
    pros: ["Good value", "Great for families", "Near Terminals 2 & 3"],
    cons: ["Taxis required for terminals"],
  },
  {
    name: "Holiday Inn Heathrow Bath Road",
    rating: 4.2,
    price: "£80 – £140/night",
    distance: "1.0 mile from Terminals 2–3",
    taxiFare: "£18 – £24",
    facilities: ["Restaurant", "Bar", "Gym", "Meeting Rooms"],
    pros: ["Modern rooms", "Menu variety", "Popular with business travellers"],
    cons: ["Not walkable to terminals"],
  },
];

const HeathrowHotels = () => {

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "CheapCabGrab – Heathrow Airport Hotels Guide",
    description:
      "Taxi guide to Sofitel Heathrow, Hilton T5, Premier Inn T4, Novotel Heathrow and Holiday Inn Bath Road. Includes fares, distance, facilities and booking options.",
    url: "https://www.cheapcabgrab.com/heathrow-hotels",
  };

  return (
    <div className="heathrow-hotels-page">

      {/* SEO */}
      <Helmet>
        <title>CheapCabGrab – Heathrow Airport Hotels & Taxi Guide</title>
        <meta
          name="description"
          content="Guide to top Heathrow hotels including Sofitel T5, Hilton T5, Novotel, Premier Inn and Holiday Inn. Distance, facilities, taxi fares and direct booking options."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-hotels" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* HERO */}
      <header className="hotels-hero">
        <h1>🏨 Heathrow Airport Hotels</h1>
        <p>
          Compare top hotels near Heathrow (T2, T3, T4, T5) with distance, facilities and
          taxi fare estimates. Book a CheapCabGrab transfer directly to your hotel.
        </p>

        <div className="hotels-hero-buttons">
          <Link to="/" className="hotels-primary-btn">🚖 Book Taxi</Link>
          <Link to="/minibus" className="hotels-secondary-btn">🚐 Minibus Transfers</Link>
          <Link to="/coach" className="hotels-secondary-btn">🚌 Coach Hire</Link>
        </div>
      </header>

      {/* INTRO */}
      <section className="hotels-section">
        <h2>⭐ Top Hotels Near Heathrow</h2>
        <p>
          Whether you're staying for business, an overnight layover or a holiday, these
          hotels offer the best mix of comfort, distance and traveller ratings.
        </p>
      </section>

      {/* HOTEL LIST */}
      <section className="hotels-list">
        {hotels.map((hotel, index) => (
          <div className="hotel-card" key={index}>
            <h3>{hotel.name}</h3>

            <div className="hotel-details">
              <p><strong>Rating:</strong> ⭐ {hotel.rating}</p>
              <p><strong>Price Range:</strong> {hotel.price}</p>
              <p><strong>Distance:</strong> {hotel.distance}</p>
              <p><strong>Taxi Fare:</strong> {hotel.taxiFare}</p>

              <h4>Facilities</h4>
              <ul>
                {hotel.facilities.map((f, i) => (
                  <li key={i}>✔ {f}</li>
                ))}
              </ul>

              <h4>Pros</h4>
              <ul>
                {hotel.pros.map((p, i) => (
                  <li key={i}>👍 {p}</li>
                ))}
              </ul>

              <h4>Cons</h4>
              <ul>
                {hotel.cons.map((c, i) => (
                  <li key={i}>⚠️ {c}</li>
                ))}
              </ul>

              <Link to="/" className="hotel-book-btn">
                🚖 Book taxi to {hotel.name.split("(")[0].trim()}
              </Link>
            </div>
          </div>
        ))}
      </section>

      {/* EXTRA SERVICES */}
      <section className="hotels-section">
        <h2>More Heathrow Travel Tools</h2>

        <div className="hotels-tools-grid">
          <Link to="/heathrow" className="hotels-tool-card">ℹ️ Heathrow Guide</Link>
          <Link to="/heathrow-taxi" className="hotels-tool-card">🚖 Heathrow Taxi</Link>
          <Link to="/terminalFinder" className="hotels-tool-card">🛫 Terminal Finder</Link>
          <Link to="/airlineContacts" className="hotels-tool-card">📞 Airline Contacts</Link>
          <Link to="/faq" className="hotels-tool-card">❓ FAQs</Link>
          <Link to="/fleet" className="hotels-tool-card">🚗 Fleet</Link>
          <Link to="/our-drivers" className="hotels-tool-card">👮 Our Drivers</Link>
        </div>
      </section>

      {/* CTA */}
      <section className="hotels-cta">
        <h2>Book Your Heathrow Hotel Transfer</h2>
        <p>Fast, reliable, fixed-price taxi service across all Heathrow hotels.</p>
        <Link to="/" className="hotels-primary-btn">🚖 Get Your Quote</Link>
      </section>

    </div>
  );
};

export default HeathrowHotels;

